import React, { useCallback, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { IconButton, Button, InlineNameEditor } from '../common/ui';

const ModuleEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [attributes, setAttributes] = useState([]);
  const [editingAttr, setEditingAttr] = useState(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('text');
  const [editRequired, setEditRequired] = useState(false);
  const [showAddAttr, setShowAddAttr] = useState(false);
  const [newAttr, setNewAttr] = useState('');
  const [newAttrType, setNewAttrType] = useState('text');
  const [newAttrRequired, setNewAttrRequired] = useState(false);
  const [newAttrUnit, setNewAttrUnit] = useState('');
  const [newAttrOptions, setNewAttrOptions] = useState([]);
  const [newAttrDefaultValue, setNewAttrDefaultValue] = useState('');
  const [newOptionInput, setNewOptionInput] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [editOptions, setEditOptions] = useState([]);
  const [editDefaultValue, setEditDefaultValue] = useState('');
  const [editOptionInput, setEditOptionInput] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      const res = await axios.get(`/api/modules/${id}`);
      setItem(res.data);
      setAttributes(res.data.attributes || []);
      setName(res.data.name);
    } catch (e) {
      setError('Failed to load module');
    }
  }, [id]);
  const [dragAttrId, setDragAttrId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const onDragStart = (attrId) => {
    setDragAttrId(attrId);
    setIsDragging(true);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const persistOrder = async (ordered) => {
    try {
      const updates = ordered.map((a, idx) => axios.put(`/api/modules/${id}/attributes/${a.id}`, { position: idx + 1 }));
      await Promise.all(updates);
    } catch (e) {
      load();
    }
  };

  const onDrop = async (e, dropAttrId) => {
    e.preventDefault();
    if (!dragAttrId || dragAttrId === dropAttrId) {
      setIsDragging(false);
      setDragAttrId(null);
      return;
    }
    const current = [...attributes];
    const fromIndex = current.findIndex(a => a.id === dragAttrId);
    const toIndex = current.findIndex(a => a.id === dropAttrId);
    if (fromIndex === -1 || toIndex === -1) {
      setIsDragging(false);
      setDragAttrId(null);
      return;
    }
    const [moved] = current.splice(fromIndex, 1);
    current.splice(toIndex, 0, moved);
    setAttributes(current);
    setIsDragging(false);
    setDragAttrId(null);
    await persistOrder(current);
  };


  useEffect(() => { load(); }, [load]);

  const addAttribute = async () => {
    if (!newAttr.trim()) return;
    
    setErrors({});
    setError('');
    const newErrors = {};
    
    // Validate dropdown requirements
    if (newAttrType === 'dropdown') {
      if (newAttrOptions.length === 0) {
        newErrors.newAttrOptions = 'Dropdown attributes must have at least one option';
      }
      if (newAttrRequired && !newAttrDefaultValue) {
        newErrors.newAttrDefaultValue = 'Required dropdown attributes must have a default value';
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    try {
      await axios.post(`/api/modules/${id}/attributes`, {
        name: newAttr.trim(),
        type: newAttrType,
        required: newAttrRequired,
        unit: newAttrUnit || null,
        allow_no_unit: 0,
        options: newAttrType === 'dropdown' ? newAttrOptions : undefined,
        default_value: newAttrType === 'dropdown' ? newAttrDefaultValue : undefined
      });
      setNewAttr('');
      setNewAttrType('text');
      setNewAttrRequired(false);
      setNewAttrUnit('');
      setNewAttrOptions([]);
      setNewAttrDefaultValue('');
      setNewOptionInput('');
      
      load();
      setShowAddAttr(false);
    } catch (e) { 
      setError(e.response?.data?.error || 'Failed to add attribute'); 
    }
  };

  const removeAttribute = async (attrId) => {
    try {
      await axios.delete(`/api/modules/${id}/attributes/${attrId}`);
      load();
    } catch (e) { setError('Failed to delete attribute'); }
  };

  const toggleAttributeVisibility = async (attrId, hidden) => {
    // Optimistic update - update UI immediately
    setAttributes(prev => prev.map(attr => 
      attr.id === attrId ? { ...attr, hidden } : attr
    ));
    
    try {
      // Sync with server in background
      await axios.put(`/api/modules/${id}/attributes/${attrId}`, { hidden });
    } catch (e) { 
      // Revert optimistic update on error
      setAttributes(prev => prev.map(attr => 
        attr.id === attrId ? { ...attr, hidden: !hidden } : attr
      ));
      setError('Failed to update attribute visibility'); 
    }
  };

  const startEditAttribute = (attr) => {
    setEditingAttr(attr);
    setEditName(attr.name);
    setEditType(attr.type || 'text');
    setEditRequired(!!attr.required);
    setEditOptions(attr.options && Array.isArray(attr.options) ? [...attr.options] : []);
    setEditDefaultValue(attr.default_value || '');
    setEditOptionInput('');
  };

  const cancelEditAttribute = () => {
    setEditingAttr(null);
    setEditName('');
    setEditType('text');
    setEditRequired(false);
    setEditOptions([]);
    setEditDefaultValue('');
    setEditOptionInput('');
  };

  const saveEditAttribute = async () => {
    if (!editingAttr) return;
    
    setErrors({});
    setError('');
    const newErrors = {};
    
    // Validate dropdown requirements
    if (editType === 'dropdown') {
      if (editOptions.length === 0) {
        newErrors.editOptions = 'Dropdown attributes must have at least one option';
      }
      if (editRequired && !editDefaultValue) {
        newErrors.editDefaultValue = 'Required dropdown attributes must have a default value';
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    try {
      await axios.put(`/api/modules/${id}/attributes/${editingAttr.id}`, {
        name: editName.trim(),
        type: editType,
        required: editRequired,
        options: editType === 'dropdown' ? editOptions : undefined,
        default_value: editType === 'dropdown' ? editDefaultValue : undefined
      });
      cancelEditAttribute();
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update attribute');
    }
  };
  
  const addNewOption = () => {
    if (newOptionInput.trim() && !newAttrOptions.includes(newOptionInput.trim())) {
      setNewAttrOptions([...newAttrOptions, newOptionInput.trim()]);
      setNewOptionInput('');
    }
  };
  
  const removeNewOption = (option) => {
    setNewAttrOptions(newAttrOptions.filter(o => o !== option));
    if (newAttrDefaultValue === option) {
      setNewAttrDefaultValue('');
    }
  };
  
  const addEditOption = () => {
    if (editOptionInput.trim() && !editOptions.includes(editOptionInput.trim())) {
      setEditOptions([...editOptions, editOptionInput.trim()]);
      setEditOptionInput('');
    }
  };
  
  const removeEditOption = (option) => {
    setEditOptions(editOptions.filter(o => o !== option));
    if (editDefaultValue === option) {
      setEditDefaultValue('');
    }
  };

  const saveName = async (newName) => {
    try {
      await axios.put(`/api/modules/${id}`, { name: newName });
      setName(newName);
      load();
    } catch (e) { 
      setError('Failed to save name');
      throw e;
    }
  };

  if (!item) {
    return (
      <div className="p-6">Loading...</div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-gray-900">Edit Module •</h1>
          <InlineNameEditor
            value={name}
            onSave={saveName}
            placeholder="Enter module name"
            className="text-2xl font-bold"
          />
        </div>
        <Button
          variant="secondary"
          onClick={() => navigate('/catalog/modules')}
        >
          Back
        </Button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      <div className="bg-white shadow rounded-lg p-4">
        <h2 className="text-lg font-semibold mb-3">Attributes</h2>
        <div className="overflow-x-auto mb-4">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Visible</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {attributes.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-3 text-center text-gray-500">No attributes</td>
                </tr>
              ) : (
                attributes.map((a) => (
                  <tr
                    key={a.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => { if (!isDragging) startEditAttribute(a); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startEditAttribute(a); } }}
                    onDragOver={onDragOver}
                    onDrop={(e) => onDrop(e, a.id)}
                    tabIndex={0}
                    role="button"
                  >
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                      <span
                        className="mr-2 cursor-grab"
                        draggable
                        onDragStart={() => onDragStart(a.id)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        ⋮⋮
                      </span>
                      <span className="mr-1 text-gray-900">{a.name}</span>
                      {a.required ? <span className="text-red-600">*</span> : null}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{a.type}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-center">
                      <input
                        type="checkbox"
                        checked={!a.hidden}
                        onChange={(e) => toggleAttributeVisibility(a.id, !e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                          className="form-checkbox h-4 w-4 text-naviter-blue transition duration-150 ease-in-out"
                        title={a.hidden ? 'Hidden from new devices' : 'Visible on new devices'}
                      />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-center">
                      <IconButton
                        variant="delete"
                        icon={Trash2}
                        onClick={(e) => { e.stopPropagation(); removeAttribute(a.id); }}
                        title="Delete"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {editingAttr && (
          <div className="mb-4 p-3 border border-gray-200 rounded-md bg-gray-50">
            <h3 className="text-sm font-medium text-gray-800 mb-2">Edit Attribute</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Attribute name" className="md:col-span-2 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-naviter-blue focus:border-transparent" />
              <select value={editType} onChange={(e) => {
                setEditType(e.target.value);
                if (e.target.value !== 'dropdown') {
                  setEditOptions([]);
                  setEditDefaultValue('');
                }
              }} className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-naviter-blue focus:border-transparent">
                <option value="text">Short text</option>
                <option value="long_text">Long description</option>
                <option value="boolean">Boolean</option>
                <option value="number">Number</option>
                <option value="percentage">Percentage</option>
                <option value="url">URL</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="dropdown">Dropdown</option>
              </select>
              <div className="flex items-center gap-2">
                <input id="m-edit-attr-required" type="checkbox" checked={editRequired} onChange={(e) => setEditRequired(e.target.checked)} />
                <label htmlFor="m-edit-attr-required" className="text-sm text-gray-700">Required</label>
              </div>
              </div>
              {editType === 'dropdown' && (
                <div className="mt-3 space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Options</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editOptionInput}
                      onChange={(e) => setEditOptionInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEditOption(); } }}
                      placeholder="Add option"
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-naviter-blue focus:border-transparent"
                    />
                    <button onClick={addEditOption} className="px-3 py-2 bg-naviter-blue text-white rounded-md hover:bg-naviter-blue-dark">Add</button>
                  </div>
                  {editOptions.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editOptions.map((option, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-sm">
                          {option}
                          <button
                            type="button"
                            onClick={() => removeEditOption(option)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {editOptions.length === 0 && (
                    <p className="text-sm text-red-600">{errors.editOptions || 'At least one option is required'}</p>
                  )}
                  {editOptions.length > 0 && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Default value</label>
                      <select
                        value={editDefaultValue}
                        onChange={(e) => {
                            setEditDefaultValue(e.target.value);
                            if (errors.editDefaultValue) setErrors(prev => ({ ...prev, editDefaultValue: undefined }));
                        }}
                        className={`w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-naviter-blue focus:border-transparent ${errors.editDefaultValue ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                      >
                        <option value="">None</option>
                        {editOptions.map((option, idx) => (
                          <option key={idx} value={option}>{option}</option>
                        ))}
                      </select>
                      {errors.editDefaultValue && (
                        <p className="text-sm text-red-600 mt-1">{errors.editDefaultValue}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={cancelEditAttribute} className="px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={saveEditAttribute} className="px-3 py-2 bg-naviter-blue text-white rounded-md hover:bg-naviter-blue-dark">Save</button>
            </div>
          </div>
        )}
        {!showAddAttr && (
          <button onClick={() => setShowAddAttr(true)} className="mb-3 px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">+ Add attribute</button>
        )}
        {showAddAttr && (
          <div className="mb-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              <input value={newAttr} onChange={(e) => setNewAttr(e.target.value)} placeholder="New attribute name" className="md:col-span-2 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-naviter-blue focus:border-transparent" />
              <select value={newAttrType} onChange={(e) => {
                setNewAttrType(e.target.value);
                if (e.target.value !== 'dropdown') {
                  setNewAttrOptions([]);
                  setNewAttrDefaultValue('');
                }
              }} className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-naviter-blue focus:border-transparent">
                <option value="text">Short text</option>
                <option value="long_text">Long description</option>
                <option value="boolean">Boolean</option>
                <option value="number">Number</option>
                <option value="percentage">Percentage</option>
                <option value="url">URL</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="dropdown">Dropdown</option>
              </select>
              <div className="flex items-center gap-2">
                <input id="m-attr-required" type="checkbox" checked={newAttrRequired} onChange={(e) => setNewAttrRequired(e.target.checked)} />
                <label htmlFor="m-attr-required" className="text-sm text-gray-700">Required</label>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => {
                  setShowAddAttr(false);
                  setNewAttr('');
                  setNewAttrType('text');
                  setNewAttrRequired(false);
                  setNewAttrOptions([]);
                  setNewAttrDefaultValue('');
                  setNewOptionInput('');
                }} className="px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={addAttribute} className="px-3 py-2 bg-naviter-blue text-white rounded-md hover:bg-naviter-blue-dark">Add</button>
              </div>
            </div>
            {newAttrType === 'dropdown' && (
              <div className="space-y-2 border-t border-gray-200 pt-3">
                <label className="block text-sm font-medium text-gray-700">Options</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newOptionInput}
                    onChange={(e) => setNewOptionInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNewOption(); } }}
                    placeholder="Add option"
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-naviter-blue focus:border-transparent"
                  />
                  <button onClick={addNewOption} className="px-3 py-2 bg-naviter-blue text-white rounded-md hover:bg-naviter-blue-dark">Add</button>
                </div>
                {newAttrOptions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newAttrOptions.map((option, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-sm">
                        {option}
                        <button
                          type="button"
                          onClick={() => removeNewOption(option)}
                          className="text-red-600 hover:text-red-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {newAttrOptions.length === 0 && (
                  <p className="text-sm text-red-600">{errors.newAttrOptions || 'At least one option is required'}</p>
                )}
                {newAttrOptions.length > 0 && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default value</label>
                    <select
                      value={newAttrDefaultValue}
                      onChange={(e) => {
                          setNewAttrDefaultValue(e.target.value);
                          if (errors.newAttrDefaultValue) setErrors(prev => ({ ...prev, newAttrDefaultValue: undefined }));
                      }}
                      className={`w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-naviter-blue focus:border-transparent ${errors.newAttrDefaultValue ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}`}
                    >
                      <option value="">None</option>
                      {newAttrOptions.map((option, idx) => (
                        <option key={idx} value={option}>{option}</option>
                      ))}
                    </select>
                    {errors.newAttrDefaultValue && (
                      <p className="text-sm text-red-600 mt-1">{errors.newAttrDefaultValue}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
      </div>
    </div>
  );
};

export default ModuleEdit;


